// Reactive <head> tag management — canonical, meta description, og:image, and
// JSON-LD, kept in sync with the current assessment as App.tsx's effect
// re-runs. Unlike gtm.ts's injectGtm() (inject once, never touch again — right
// for a permanent analytics container), these tags describe THIS page's
// content and must update if the resolved assessment changes, so each
// upsert-by-id finds-or-creates its element and overwrites its content on
// every call rather than skipping once present.

function upsertLink(id: string, rel: string, href: string): void {
  let el = document.getElementById(id) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.id = id;
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertMeta(id: string, attr: 'name' | 'property', key: string, content: string): void {
  let el = document.getElementById(id) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.id = id;
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Self-canonical — the current URL with query params stripped. Sufficient
 *  for a single-page-per-slug app with no twin-content pages competing for
 *  the same query (a real hreflang pairing is only needed if two pages are
 *  meant to rank for the SAME search — Summit's AU/NA pages aren't). */
export function setCanonical(): void {
  const clean = window.location.origin + window.location.pathname;
  upsertLink('ee-canonical', 'canonical', clean);
}

export function setMetaDescription(description: string): void {
  if (!description) return;
  upsertMeta('ee-meta-description', 'name', 'description', description);
}

/** Per-brand fallback — always a real, branded image (the resolved brand's
 *  own logo), never a generic non-branded default. A purpose-built social
 *  share card per event is a separate content task; this just stops the
 *  page from advertising with no image or the wrong brand's mark. */
export function setOgImage(imageUrl: string | null | undefined): void {
  if (!imageUrl) return;
  upsertMeta('ee-og-image', 'property', 'og:image', imageUrl);
}

/** Upsert (or remove, when `data` is null) the page's JSON-LD script tag. */
export function setJsonLd(data: Record<string, unknown> | null): void {
  const id = 'ee-jsonld-event';
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  let el = existing as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
